import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Tooltip,
    Alert,
    Button,
    CircularProgress,
    useMediaQuery,
    useTheme,
    Divider
} from '@mui/material';
import { motion } from 'framer-motion';
import { 
    Shield, 
    Upload, 
    Info, 
    CheckCircle, 
    AlertCircle, 
    RefreshCw,
    Key,
    Copy,
    Eye,
    EyeOff,
    Bug,
    AlertTriangle
} from 'lucide-react';
import Sidebar from '../Sidebar.js';
import './ProfileVerification.css';
import { useAuth0 } from '@auth0/auth0-react';

// Import services
import { paymentService, pinVerificationService } from '../../../services';
import { trackCommitted } from '../../../utils/analytics';

const ProfileVerification = () => {
    const { getAccessTokenSilently, user } = useAuth0();
    const theme = useTheme();
    
    // Responsive breakpoints
    const isMobile = useMediaQuery('(max-width:599px)');
    const isTablet = useMediaQuery('(min-width:600px) and (max-width:1024px)');
    const useCompactLayout = isMobile;
    
    // Sidebar state
    const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
    const [darkMode, setDarkMode] = useState(false);
    
    // Stripe verification state
    const [verificationLoading, setVerificationLoading] = useState(false);
    const [verificationStatus, setVerificationStatus] = useState('loading');
    const [error, setError] = useState(null);
    
    // PIN state - simplified without status
    const [pinState, setPinState] = useState({
        memberPin: '',
        agentPin: '',
        hasValidPins: false,
        pinsLoading: false,
        pinsError: null,
        generatedAt: null,
        expiresAt: null,
        usedCount: 0
    });
    
    // SIMPLIFIED Rate limiting state
    const [hasGeneratedToday, setHasGeneratedToday] = useState(false);
    const [lastGeneratedTime, setLastGeneratedTime] = useState(null);
    
    // Debug state
    const [showDebug, setShowDebug] = useState(false);
    
    // PIN visibility toggles
    const [showMemberPin, setShowMemberPin] = useState(true);
    const [showAgentPin, setShowAgentPin] = useState(true);
    
    // PIN verification state
    const [verificationState, setVerificationState] = useState({
        isVerifying: false,
        verificationSuccess: false,
        verificationError: null,
        verifiedAt: null
    });

    // Update sidebar state when screen size changes
    useEffect(() => {
        setSidebarOpen(!isMobile);
    }, [isMobile]);
    
    // Check verification status and PIN status on component mount
    useEffect(() => {
        checkVerificationStatus();
        checkPinStatus();
    }, []);

    // SIMPLIFIED Rate limiting check
    const canGenerateNewPins = () => {
        if (!pinState.generatedAt) {
            console.log('✅ No PINs generated yet - allowing generation');
            return true; // No PINs generated yet
        }
        
        const now = new Date();
        const generated = new Date(pinState.generatedAt);
        const hoursSinceGeneration = (now - generated) / (1000 * 60 * 60);
        
        // For testing: 5 minutes = 5/60 hours = 0.083 hours
        // For production: 24 hours
        const waitTimeHours = 289.16; // Change to 24 for production
        
        const canGenerate = hoursSinceGeneration >= waitTimeHours;
        
        console.log('🔍 Rate limit check:', {
            generatedAt: pinState.generatedAt,
            hoursSinceGeneration: hoursSinceGeneration.toFixed(2),
            waitTimeHours,
            canGenerate
        });
        
        return canGenerate;
    };

    // Get time remaining for rate limit
    const getTimeRemaining = () => {
        if (!pinState.generatedAt) return null;
        
        const now = new Date();
        const generated = new Date(pinState.generatedAt);
        const waitTimeMs = 5 * 60 * 1000; // 5 minutes for testing (change to 24 * 60 * 60 * 1000 for production)
        const nextAvailable = new Date(generated.getTime() + waitTimeMs);
        const remaining = nextAvailable - now;
        
        if (remaining <= 0) return null;
        
        const minutes = Math.ceil(remaining / (1000 * 60));
        return minutes;
    };

    // Enhanced PIN status check
    const checkPinStatus = async () => {
        try {
            console.log('🔍 Checking PIN status...');
            setPinState(prev => ({ ...prev, pinsLoading: true, pinsError: null }));
            
            const token = await getAccessTokenSilently({
                audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                scope: 'openid profile email'
            });
            
            console.log('✅ Auth token obtained for PIN status check');
            console.log('🌐 Making API call to PIN status endpoint...');
            
            const response = await pinVerificationService.getPinStatus(token);
            console.log('✅ PIN status response received:', response.data);
            
            const data = response.data;
            
            setPinState(prev => ({
                ...prev,
                hasValidPins: data.pins_valid,
                memberPin: data.member_pin || '',
                agentPin: data.agent_pin || '',
                generatedAt: data.generated_at,
                expiresAt: data.expires_at,
                usedCount: data.used_count || 0,
                pinsLoading: false
            }));
            
        } catch (error) {
            console.error('❌ Error checking PIN status:', error);
            console.error('Error response:', error.response?.data);
            
            setPinState(prev => ({
                ...prev,
                pinsError: error.friendlyMessage || error.message || 'Failed to check PIN status',
                pinsLoading: false
            }));
        }
    };

    // Stripe verification functions (keeping existing logic)
    const checkVerificationStatus = async () => {
        try {
            setVerificationStatus('loading');
            setError(null);
            
            const token = await getAccessTokenSilently({
                audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                scope: 'openid profile email'
            });
            
            const response = await paymentService.getStripeVerificationStatus(token);
            const data = response.data;
            
            if (data.stripe_verified) {
                setVerificationStatus('verified');
            } else if (data.verification_failed) {
                setVerificationStatus('failed');
            } else {
                setVerificationStatus('unverified');
            }
        } catch (error) {
            console.error('Error checking verification status:', error);
            setError(error.friendlyMessage || 'Unable to check verification status. Please try again later.');
            setVerificationStatus('unverified');
        }
    };

    const handleStripeVerification = async () => {
        try {
            // committed: this is the ID step (CLAUDE.md) — fires when Stripe
            // Identity verification starts.
            trackCommitted();
            setVerificationLoading(true);
            setError(null);

            const token = await getAccessTokenSilently({
                audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                scope: 'openid profile email'
            });
            
            const response = await paymentService.createIdentityVerification({}, token);
            const data = response.data;
            
            if (data.url) {
                console.log('Redirecting to Stripe verification URL:', data.url);
                window.location.href = data.url;
            } else if (data.client_secret) {
                window.location.href = `https://verify.stripe.com/?client_secret=${data.client_secret}`;
            } else {
                throw new Error('No verification URL provided');
            }
            
        } catch (error) {
            console.error('Verification error:', error);
            setError(error.friendlyMessage || 'Failed to start verification process. Please try again later.');
            setVerificationStatus('failed');
            setVerificationLoading(false);
        }
    };

    // Enhanced generatePins with simplified rate limiting
    const generatePins = async () => {
        // SIMPLE RATE LIMIT CHECK
        if (!canGenerateNewPins()) {
            const timeLeft = getTimeRemaining();
            alert(`Please wait ${timeLeft} more minutes before generating new PINs.`);
            return;
        }

        try {
            console.log('🔄 Starting PIN generation...');
            setPinState(prev => ({ ...prev, pinsLoading: true, pinsError: null }));
            
            const token = await getAccessTokenSilently({
                audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                scope: 'openid profile email'
            });
            
            console.log('✅ Auth token obtained for PIN generation');
            
            const response = await pinVerificationService.generatePins(token);
            console.log('✅ PIN generation response:', response.data);
            
            const data = response.data;
            
            // Update PIN state without status calculation
            setPinState(prev => ({
                ...prev,
                hasValidPins: true,
                memberPin: data.member_pin,
                agentPin: data.agent_pin,
                generatedAt: data.generated_at,
                expiresAt: data.expires_at,
                usedCount: 0,
                pinsLoading: false,
                pinsError: null
            }));
            
            // Set rate limit flags
            setHasGeneratedToday(true);
            setLastGeneratedTime(new Date(data.generated_at));
            
            console.log('✅ PINs generated successfully with rate limit applied');
            
        } catch (error) {
            console.error('❌ Error generating PINs:', error);
            setPinState(prev => ({
                ...prev,
                pinsError: error.friendlyMessage || error.message || 'Failed to generate PINs',
                pinsLoading: false
            }));
        }
    };

    // Enhanced regeneratePins with simplified rate limiting
    const regeneratePins = async () => {
        // SIMPLE RATE LIMIT CHECK
        if (!canGenerateNewPins()) {
            const timeLeft = getTimeRemaining();
            alert(`Please wait ${timeLeft} more minutes before generating new PINs.`);
            console.log('🚫 Generation blocked by rate limit');
            return;
        }

        try {
            console.log('🔄 Starting PIN regeneration...');
            setPinState(prev => ({ ...prev, pinsLoading: true, pinsError: null }));
            
            const token = await getAccessTokenSilently({
                audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                scope: 'openid profile email'
            });
            
            const response = await pinVerificationService.regeneratePins(token);
            console.log('✅ PIN regeneration response:', response.data);
            
            const data = response.data;
            
            // Update PIN state without status calculation
            setPinState(prev => ({
                ...prev,
                hasValidPins: true,
                memberPin: data.member_pin,
                agentPin: data.agent_pin,
                generatedAt: data.generated_at,
                expiresAt: data.expires_at,
                usedCount: 0,
                pinsLoading: false,
                pinsError: null
            }));
            
            // Set rate limit flags
            setHasGeneratedToday(true);
            setLastGeneratedTime(new Date(data.generated_at));
            
            // Reset verification state since we have new PINs
            setVerificationState({
                isVerifying: false,
                verificationSuccess: false,
                verificationError: null,
                verifiedAt: null
            });
            
            console.log('✅ PINs regenerated successfully with rate limit applied');
            
        } catch (error) {
            console.error('❌ Error regenerating PINs:', error);
            setPinState(prev => ({
                ...prev,
                pinsError: error.friendlyMessage || 'Failed to regenerate PINs',
                pinsLoading: false
            }));
        }
    };

    const copyToClipboard = async (text, type) => {
        try {
            await navigator.clipboard.writeText(text);
            console.log(`${type} PIN copied to clipboard`);
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleToggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    const getContentMargin = () => {
        if (isMobile) return 0;
        return sidebarOpen ? '240px' : '80px';
    };

    // SIMPLE GENERATE BUTTON RENDER
    const renderGenerateButton = () => {
        const canGenerate = canGenerateNewPins();
        const isDisabled = !canGenerate || pinState.pinsLoading;
        const timeLeft = getTimeRemaining();
        
        let timeMessage = '';
        if (!canGenerate && timeLeft) {
            timeMessage = `Available in ${timeLeft} minutes`;
        }
        
        console.log('🔍 Button render state:', {
            canGenerate,
            isDisabled,
            timeLeft,
            pinsLoading: pinState.pinsLoading
        });
        
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Button
                    variant="outlined"
                    startIcon={pinState.pinsLoading ? <CircularProgress size={16} /> : <RefreshCw />}
                    onClick={regeneratePins}
                    disabled={isDisabled}
                    size="small"
                    sx={{ 
                        textTransform: 'none',
                        opacity: isDisabled ? 0.6 : 1,
                        backgroundColor: isDisabled ? '#f5f5f5' : 'white',
                        color: isDisabled ? '#666' : '#333'
                    }}
                >
                    {pinState.pinsLoading ? 'Generating...' : 'Generate New PINs'}
                </Button>
                
                {timeMessage && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                        {timeMessage}
                    </Typography>
                )}
                
                {/* Debug info - remove in production */}
                <Typography variant="caption" sx={{ mt: 0.2, fontSize: '10px', color: '#999' }}>
                    Can generate: {canGenerate ? '✅' : '❌'}
                </Typography>
            </Box>
        );
    };

    // Render Stripe verification section (keeping existing logic)
    const renderVerificationSection = () => {
        if (verificationStatus === 'loading') {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: 3 }}>
                    <CircularProgress size={24} sx={{ color: '#FF4775', mr: 2 }} />
                    <Typography>Checking verification status...</Typography>
                </Box>
            );
        } else if (verificationStatus === 'verified') {
            return (
                <Box sx={{ my: 2 }}>
                    <Box sx={{ 
                        display: 'flex',
                        flexDirection: useCompactLayout ? 'column' : 'row',
                        alignItems: useCompactLayout ? 'flex-start' : 'center',
                        bgcolor: 'rgba(76, 175, 80, 0.1)',
                        p: useCompactLayout ? 1.5 : 2,
                        borderRadius: 1,
                        border: '1px solid rgba(76, 175, 80, 0.2)'
                    }}>
                        <CheckCircle 
                            size={useCompactLayout ? 20 : 24} 
                            color="#4CAF50" 
                            style={{ 
                                marginRight: useCompactLayout ? 0 : 12, 
                                marginBottom: useCompactLayout ? 8 : 0, 
                                flexShrink: 0,
                                alignSelf: useCompactLayout ? 'center' : 'flex-start'
                            }} 
                        />
                        <Box>
                            <Typography sx={{ 
                                fontWeight: 500, 
                                color: '#4CAF50',
                                textAlign: useCompactLayout ? 'center' : 'left',
                                mb: 0.5
                            }}>
                                Identity Verified
                            </Typography>
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    color: '#666',
                                    textAlign: useCompactLayout ? 'center' : 'left'
                                }}
                            >
                                Your identity has been successfully verified. Thank you for helping build trust in your circle.
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            );
        } else if (verificationStatus === 'failed') {
            return (
                <Box sx={{ my: 2 }}>
                    <Alert
                        severity="error"
                        sx={{
                            mb: 2,
                            backgroundColor: 'rgba(244, 67, 54, 0.1)',
                            color: '#666',
                            '& .MuiAlert-icon': {
                                color: '#F44336'
                            }
                        }}
                    >
                        <Box>
                            <Typography sx={{ fontWeight: 500, mb: 0.5 }}>
                                Verification Failed
                            </Typography>
                            <Typography variant="body2">
                                We encountered an issue with your verification. This could be due to image quality or information mismatch. Please try again.
                            </Typography>
                        </Box>
                    </Alert>
                    
                    {error && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    
                    <Box sx={{ display: 'flex', justifyContent: useCompactLayout ? 'center' : 'flex-start' }}>
                        <Button
                            variant="contained"
                            startIcon={verificationLoading ? <CircularProgress size={20} color="inherit" /> : <Upload />}
                            onClick={handleStripeVerification}
                            disabled={verificationLoading}
                            sx={{
                                mt: 2,
                                bgcolor: '#FF4775',
                                color: 'white',
                                '&:hover': { bgcolor: '#FF2665' },
                                textTransform: 'none',
                                width: useCompactLayout ? '100%' : 'auto'
                            }}
                        >
                            {verificationLoading ? 'Loading...' : 'Try Again'}
                        </Button>
                    </Box>
                </Box>
            );
        } else {
            return (
                <>
                    <Alert
                        severity="info"
                        sx={{
                            my: 2,
                            backgroundColor: 'rgba(255, 71, 117, 0.1)',
                            color: '#666',
                            '& .MuiAlert-icon': { color: '#FF4775' }
                        }}
                    >
                        We use Stripe Identity, a secure verification service that encrypts and protects your information.
                    </Alert>
                    
                    {error && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    
                    <Box sx={{ display: 'flex', justifyContent: useCompactLayout ? 'center' : 'flex-start' }}>
                        <Button
                            variant="contained"
                            startIcon={verificationLoading ? <CircularProgress size={20} color="inherit" /> : <Upload />}
                            onClick={handleStripeVerification}
                            disabled={verificationLoading}
                            sx={{
                                mt: 2,
                                bgcolor: '#FF4775',
                                color: 'white',
                                '&:hover': { bgcolor: '#FF2665' },
                                textTransform: 'none',
                                width: useCompactLayout ? '100%' : 'auto'
                            }}
                        >
                            {verificationLoading ? 'Loading...' : 'Verify me!'}
                        </Button>
                    </Box>
                </>
            );
        }
    };

    // Enhanced PIN section with simplified rate limiting
    const renderPinSection = () => {
        return (
            <Box sx={{ mt: 4 }}>
                {/* Debug section - only show if there are issues */}
                {pinState.pinsError && (
                    <Box sx={{ mb: 3 }}>
                        <Button
                            startIcon={<Bug />}
                            onClick={() => setShowDebug(!showDebug)}
                            size="small"
                            sx={{ mb: 1 }}
                        >
                            {showDebug ? 'Hide' : 'Show'} Debug Info
                        </Button>
                        
                        {showDebug && (
                            <Paper sx={{ p: 2, bgcolor: '#f8f9fa', border: '1px solid #ddd' }}>
                                <Typography variant="subtitle2" gutterBottom>PIN State:</Typography>
                                <pre style={{ fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {JSON.stringify(pinState, null, 2)}
                                </pre>
                                
                                <Box sx={{ mt: 2 }}>
                                    <Button onClick={checkPinStatus} size="small" variant="outlined">
                                        Retry PIN Status
                                    </Button>
                                </Box>
                            </Paper>
                        )}
                    </Box>
                )}

                {/* Simple header without status */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                        Authentication PINs
                    </Typography>
                </Box>

                {/* Loading State */}
                {pinState.pinsLoading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={24} sx={{ color: '#FF4775', mr: 2 }} />
                        <Typography>Updating your PINs...</Typography>
                    </Box>
                )}

                {/* Error State */}
                {pinState.pinsError && !pinState.pinsLoading && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Unable to update your PINs
                        </Typography>
                        <Typography variant="body2">
                            {pinState.pinsError}
                        </Typography>
                        <Button onClick={checkPinStatus} size="small" sx={{ mt: 1 }}>
                            Try Again
                        </Button>
                    </Alert>
                )}

                {/* No PINs State */}
                {!pinState.hasValidPins && !pinState.pinsLoading && !pinState.pinsError && (
                    <Box sx={{ 
                        textAlign: 'center', 
                        py: 4,
                        bgcolor: 'rgba(0, 0, 0, 0.02)',
                        borderRadius: 2,
                        border: '1px dashed #ddd'
                    }}>
                        <Key size={48} style={{ color: '#FF4775', marginBottom: 16 }} />
                        <Typography variant="h6" gutterBottom>
                            Generate Your First PINs
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                            Generate secure 6-digit PINs for two-way verification with our support team.
                        </Typography>
                        
                        <Button
                            variant="contained"
                            startIcon={<Key />}
                            onClick={generatePins}
                            disabled={!canGenerateNewPins() || pinState.pinsLoading}
                            sx={{
                                bgcolor: '#FF4775',
                                color: 'white',
                                '&:hover': { bgcolor: '#FF2665' },
                                textTransform: 'none',
                                px: 4,
                                py: 1.5
                            }}
                        >
                            Generate PINs
                        </Button>
                        
                        {!canGenerateNewPins() && getTimeRemaining() && (
                            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 2 }}>
                                Available in {getTimeRemaining()} minutes
                            </Typography>
                        )}
                    </Box>
                )}

                {/* Display PINs */}
                {pinState.hasValidPins && !pinState.pinsLoading && (
                    <Box>
                        {/* PIN Display Cards */}
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', md: 'row' }, 
                            gap: 3, 
                            mb: 3 
                        }}>
                            {/* Member PIN */}
                            <Paper sx={{ 
                                flex: 1, 
                                p: 3, 
                                bgcolor: 'rgba(76, 175, 80, 0.05)',
                                border: '1px solid rgba(76, 175, 80, 0.2)'
                            }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                        Member PIN
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            size="small"
                                            onClick={() => setShowMemberPin(!showMemberPin)}
                                            sx={{ minWidth: 32, p: 0.5 }}
                                        >
                                            {showMemberPin ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </Button>
                                        <Button
                                            size="small"
                                            onClick={() => copyToClipboard(pinState.memberPin, 'Member')}
                                            sx={{ minWidth: 32, p: 0.5 }}
                                        >
                                            <Copy size={16} />
                                        </Button>
                                    </Box>
                                </Box>
                                
                                <Typography 
                                    variant="h4" 
                                    fontFamily="monospace" 
                                    textAlign="center"
                                    sx={{ 
                                        letterSpacing: '0.5em',
                                        color: '#4CAF50',
                                        fontWeight: 600
                                    }}
                                >
                                    {showMemberPin ? pinState.memberPin : '••••••'}
                                </Typography>
                                
                                <Typography variant="caption" color="textSecondary" textAlign="center" display="block" sx={{ mt: 1 }}>
                                    Share this PIN with support when requested
                                </Typography>
                            </Paper>

                            {/* Agent PIN */}
                            <Paper sx={{ 
                                flex: 1, 
                                p: 3, 
                                bgcolor: 'rgba(33, 150, 243, 0.05)',
                                border: '1px solid rgba(33, 150, 243, 0.2)'
                            }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                        Agent PIN
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            size="small"
                                            onClick={() => setShowAgentPin(!showAgentPin)}
                                            sx={{ minWidth: 32, p: 0.5 }}
                                        >
                                            {showAgentPin ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </Button>
                                        <Button
                                            size="small"
                                            onClick={() => copyToClipboard(pinState.agentPin, 'Agent')}
                                            sx={{ minWidth: 32, p: 0.5 }}
                                        >
                                            <Copy size={16} />
                                        </Button>
                                    </Box>
                                </Box>
                                
                                <Typography 
                                    variant="h4" 
                                    fontFamily="monospace" 
                                    textAlign="center"
                                    sx={{ 
                                        letterSpacing: '0.5em',
                                        color: '#2196F3',
                                        fontWeight: 600
                                    }}
                                >
                                    {showAgentPin ? pinState.agentPin : '••••••'}
                                </Typography>
                                
                                <Typography variant="caption" color="textSecondary" textAlign="center" display="block" sx={{ mt: 1 }}>
                                    Ask support to verify this PIN with you
                                </Typography>
                            </Paper>
                        </Box>

                        {/* PIN Metadata and Generate Button */}
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between', 
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            gap: 2,
                            mb: 3,
                            p: 2,
                            bgcolor: 'rgba(0, 0, 0, 0.02)',
                            borderRadius: 1
                        }}>
                            <Box>
                                <Typography variant="caption" color="textSecondary">
                                    Generated: {formatDateTime(pinState.generatedAt)}
                                </Typography>
                                {pinState.expiresAt && (
                                    <Typography variant="caption" color="textSecondary" display="block">
                                        Expires: {formatDateTime(pinState.expiresAt)}
                                    </Typography>
                                )}
                                {pinState.usedCount > 0 && (
                                    <Typography variant="caption" color="textSecondary" display="block">
                                        Used {pinState.usedCount} time{pinState.usedCount !== 1 ? 's' : ''}
                                    </Typography>
                                )}
                            </Box>
                            
                            {renderGenerateButton()}
                        </Box>

                        {/* Usage Instructions */}
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                How to use your PINs:
                            </Typography>
                            <Typography variant="body2" component="div">
                                1. When contacting support, provide your <strong>Member PIN</strong><br/>
                                2. Ask the support agent to verify their <strong>Agent PIN</strong> with you<br/>
                                3. Both PINs must match for secure verification
                            </Typography>
                        </Alert>
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'grey.50' }}>
            <Sidebar 
                onToggle={handleToggleSidebar}
                darkMode={darkMode}
            />
            
            <Box
                component={motion.div}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
                sx={{
                    flexGrow: 1,
                    ml: !isMobile ? getContentMargin() : 0,
                    transition: 'margin-left 0.3s',
                    p: useCompactLayout ? 2 : 3,
                    width: '100%'
                }}
            >
                <Container 
                    maxWidth={useCompactLayout ? "sm" : "md"} 
                    sx={{ 
                        px: useCompactLayout ? 1 : 3,
                        width: '100%',
                        overflowX: 'hidden'
                    }}
                >
                    {/* Header Section */}
                    <Box sx={{ 
                        textAlign: 'center', 
                        mb: useCompactLayout ? 3 : 4
                    }}>
                        <Box sx={{ 
                            width: useCompactLayout ? 64 : 80,
                            height: useCompactLayout ? 64 : 80,
                            borderRadius: '50%',
                            bgcolor: '#FF4775',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto',
                            mb: useCompactLayout ? 1.5 : 2
                        }}>
                            <Shield size={useCompactLayout ? 32 : 40} color="white" />
                        </Box>
                        <Typography 
                            variant={useCompactLayout ? "h5" : "h4"} 
                            gutterBottom
                        >
                            Trust & Verification
                        </Typography>
                        <Typography 
                            color="textSecondary" 
                            sx={{ 
                                mb: useCompactLayout ? 2 : 3, 
                                maxWidth: 600, 
                                mx: 'auto',
                                fontSize: useCompactLayout ? '0.875rem' : '1rem'
                            }}
                        >
                            Building trust within your circle is essential. Complete both verification steps to show other members you're committed to a safe and reliable group experience.
                        </Typography>
                    </Box>
                    
                    {/* Identity Verification Section */}
                    <Paper 
                        sx={{ 
                            p: useCompactLayout ? 2 : 4, 
                            mb: useCompactLayout ? 2 : 3,
                            borderRadius: useCompactLayout ? '12px' : '16px',
                            maxWidth: '100%'
                        }} 
                        elevation={1}
                    >
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: useCompactLayout ? 'column' : 'row',
                            alignItems: useCompactLayout ? 'center' : 'flex-start', 
                            justifyContent: 'space-between', 
                            mb: 2 
                        }}>
                            <Box sx={{ 
                                textAlign: useCompactLayout ? 'center' : 'left',
                                mb: useCompactLayout ? 1 : 0
                            }}>
                                <Typography variant="h6" gutterBottom>
                                    Identity Verification
                                </Typography>
                                <Typography 
                                    color="textSecondary" 
                                    gutterBottom
                                    sx={{ fontSize: useCompactLayout ? '0.875rem' : '1rem' }}
                                >
                                    Protect your circle by verifying your identity with a government-issued ID
                                </Typography>
                            </Box>
                            <Tooltip title="Your verification helps create trust within your circle">
                                <Info 
                                    size={20} 
                                    style={{ 
                                        color: '#FF4775', 
                                        marginLeft: useCompactLayout ? 0 : '8px',
                                        marginTop: useCompactLayout ? '4px' : 0,
                                        flexShrink: 0 
                                    }} 
                                />
                            </Tooltip>
                        </Box>
                        
                        {renderVerificationSection()}
                    </Paper>
                    
                    {/* PIN Authentication Section */}
                    <Paper 
                        sx={{ 
                            p: useCompactLayout ? 2 : 4,
                            borderRadius: useCompactLayout ? '12px' : '16px',
                            maxWidth: '100%'
                        }} 
                        elevation={1}
                    >
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: useCompactLayout ? 'column' : 'row',
                            alignItems: useCompactLayout ? 'center' : 'flex-start', 
                            justifyContent: 'space-between', 
                            mb: 2 
                        }}>
                            <Box sx={{ 
                                textAlign: useCompactLayout ? 'center' : 'left',
                                mb: useCompactLayout ? 1 : 0
                            }}>
                                <Typography variant="h6" gutterBottom>
                                    PIN Authentication
                                </Typography>
                                <Typography 
                                    color="textSecondary"
                                    sx={{ fontSize: useCompactLayout ? '0.875rem' : '1rem' }}
                                >
                                    This two-way verification ensures both you and our support team can trust each other
                                </Typography>
                            </Box>
                            <Tooltip title="Secure PIN-based verification system">
                                <Info 
                                    size={20} 
                                    style={{ 
                                        color: '#FF4775', 
                                        marginLeft: useCompactLayout ? 0 : '8px', 
                                        marginTop: useCompactLayout ? '4px' : 0,
                                        flexShrink: 0 
                                    }} 
                                />
                            </Tooltip>
                        </Box>
                        
                        {renderPinSection()}
                    </Paper>
                </Container>
            </Box>
        </Box>
    );
};

export default ProfileVerification;